import styled, { css } from "styled-components";
import { toolbarButtonCss } from "../SubtitlesBlock/styled";
import { Button } from "antd";

export const SettingsButtonSC = styled(Button)`
    position: absolute;
    bottom: 20px;
    left: 20px;
    padding: 0;
    height: auto;
`;

/** Whose work each group belongs to — the colour of the stripe next to its title. */
export const GROUP_ACCENTS = {
    mode: "#d4380d",
    playback: "#1677ff",
    view: "#1677ff",
    source: "#722ed1",
    translation: "#13c2c2",
    voicing: "#fa8c16",
    publish: "#52c41a",
    service: "#8c8c8c",
} as const;

export type GroupAccent = keyof typeof GROUP_ACCENTS;

export const PanelScopeSC = styled.div`
    padding: 10px 16px;
    background: #f0f5ff;
    border-bottom: 1px solid #d6e4ff;
    font-size: 12px;
    line-height: 1.5;
    color: #1d39c4;

    b {
        font-weight: 600;
    }
`;

export const GroupSC = styled.section`
    padding: 14px 16px 4px;

    & + & {
        border-top: 1px solid #f0f0f0;
    }
`;

export const GroupTitleSC = styled.h4<{ $accent: GroupAccent }>`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 2px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #262626;

    &::before {
        content: "";
        width: 3px;
        height: 14px;
        border-radius: 2px;
        background: ${({ $accent }) => GROUP_ACCENTS[$accent]};
    }
`;

export const GroupHintSC = styled.p`
    margin: 0 0 8px 11px;
    font-size: 12px;
    line-height: 1.45;
    color: #8c8c8c;
`;

export const RowSC = styled.div<{ $wide: boolean; $danger: boolean }>`
    display: flex;
    ${({ $wide }) =>
        $wide
            ? css`
                  flex-direction: column;
                  align-items: stretch;
                  row-gap: 6px;
              `
            : css`
                  align-items: flex-start;
                  justify-content: space-between;
                  column-gap: 12px;
              `}
    padding: 8px 10px;
    margin-bottom: 6px;
    border: 1px solid ${({ $danger }) => ($danger ? "#ffccc7" : "#f0f0f0")};
    border-radius: 6px;
    background: ${({ $danger }) => ($danger ? "#fff8f7" : "#fafafa")};
    font-weight: 400;

    &:hover {
        border-color: ${({ $danger }) => ($danger ? "#ffa39e" : "#d9d9d9")};
    }
`;

export const RowTextSC = styled.div`
    min-width: 0;
    flex: 1 1 auto;
`;

export const RowTitleSC = styled.div`
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    color: #262626;
`;

export const RowHintSC = styled.div`
    margin-top: 2px;
    font-size: 11.5px;
    line-height: 1.45;
    color: #8c8c8c;
`;

/** Why the row's button does nothing right now: the working mode switched it off. */
export const RowOffSC = styled.div`
    margin-top: 3px;
    font-size: 11.5px;
    line-height: 1.45;
    color: ${GROUP_ACCENTS.mode};
`;

/**
 * The control side of a row.
 *
 * Everything here is a button written somewhere else — an antd Button, a UiKit ButtonH, a bare
 * `.toolbar-btn` div portalled out of a toolbar — so the row normalises what it can (size,
 * alignment, wrapping) and carries the toolbar's own button rules, which stop applying to a
 * portalled child the moment it leaves the toolbar's DOM subtree.
 */
export const RowControlSC = styled.div<{ $wide: boolean }>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    ${({ $wide }) => ($wide ? "" : "justify-content: flex-end; flex: 0 1 auto; max-width: 52%;")}
    font-weight: 400;

    ${toolbarButtonCss}

    /* The panel is narrow: a long label wraps rather than pushing the row sideways. */
    .toolbar-btn {
        white-space: normal;
        text-align: left;
    }
`;
